import { supabase } from '../lib/supabase';


/**
 * Notification Service
 * Handles in-app notifications and email notifications for voice actors
 */

export const notificationService = {
  /**
   * Get notifications for current user
   * @param {Object} filters - Filter options
   * @param {boolean} filters.unreadOnly - Only fetch unread notifications
   * @param {number} filters.limit - Limit number of results
   */
  async getNotifications({ unreadOnly = false, limit = 50 } = {}) {
    try {
      let query = supabase
        ?.from('notifications')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (unreadOnly) {
        query = query?.eq('read', false);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(notification => ({
        id: notification?.id,
        userId: notification?.user_id,
        type: notification?.type,
        title: notification?.title,
        message: notification?.message,
        metadata: notification?.metadata,
        read: notification?.read,
        timestamp: notification?.created_at
      })) || [];
    } catch (err) {
      console.error('Error fetching notifications:', err);
      throw err;
    }
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount() {
    try {
      const { count, error } = await supabase
        ?.from('notifications')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('read', false);

      if (error) throw error;

      return count || 0;
    } catch (err) {
      console.error('Error fetching unread count:', err);
      throw err;
    }
  },

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   */
  async markAsRead(notificationId) {
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.update({ read: true })
        ?.eq('id', notificationId);

      if (error) throw error;

      return true;
    } catch (err) {
      console.error('Error marking notification as read:', err);
      throw err;
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        ?.from('notifications')
        ?.update({ read: true })
        ?.eq('user_id', user?.id)
        ?.eq('read', false);

      if (error) throw error;

      return true;
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      throw err;
    }
  },

  /**
   * Delete notification
   * @param {string} notificationId - Notification ID
   */
  async deleteNotification(notificationId) {
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.delete()
        ?.eq('id', notificationId);

      if (error) throw error;

      return true;
    } catch (err) {
      console.error('Error deleting notification:', err);
      throw err;
    }
  },

  /**
   * Delete all notifications
   */
  async deleteAllNotifications() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        ?.from('notifications')
        ?.delete()
        ?.eq('user_id', user?.id);

      if (error) throw error;

      return true;
    } catch (err) {
      console.error('Error deleting all notifications:', err);
      throw err;
    }
  },

  /**
   * Get notification preferences
   */
  async getNotificationPreferences() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        ?.from('notification_preferences')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      // Return defaults if no preferences exist
      if (!data) {
        return {
          emailNewFollower: true,
          emailDailySummary: false,
          emailWeeklySummary: true,
          inAppNewFollower: true
        };
      }

      return {
        id: data?.id,
        userId: data?.user_id,
        emailNewFollower: data?.email_new_follower,
        emailDailySummary: data?.email_daily_summary,
        emailWeeklySummary: data?.email_weekly_summary,
        inAppNewFollower: data?.in_app_new_follower,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (err) {
      console.error('Error fetching notification preferences:', err);
      throw err;
    }
  },

  /**
   * Update notification preferences
   * @param {Object} preferences - Preference settings
   */
  async updateNotificationPreferences(preferences) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) throw new Error('User not authenticated');

      const updateData = {
        user_id: user?.id,
        email_new_follower: preferences?.emailNewFollower,
        email_daily_summary: preferences?.emailDailySummary,
        email_weekly_summary: preferences?.emailWeeklySummary,
        in_app_new_follower: preferences?.inAppNewFollower,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase
        ?.from('notification_preferences')
        ?.upsert(updateData, { onConflict: 'user_id' })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        emailNewFollower: data?.email_new_follower,
        emailDailySummary: data?.email_daily_summary,
        emailWeeklySummary: data?.email_weekly_summary,
        inAppNewFollower: data?.in_app_new_follower,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (err) {
      console.error('Error updating notification preferences:', err);
      throw err;
    }
  },

  /**
   * Subscribe to real-time notifications
   * @param {Function} callback - Callback function for new notifications
   */
  subscribeToNotifications(callback) {
    const subscription = supabase
      ?.channel('notifications')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${supabase?.auth?.getUser()?.then(({ data }) => data?.user?.id)}`
        },
        (payload) => {
          const notification = {
            id: payload?.new?.id,
            userId: payload?.new?.user_id,
            type: payload?.new?.type,
            title: payload?.new?.title,
            message: payload?.new?.message,
            metadata: payload?.new?.metadata,
            read: payload?.new?.read,
            timestamp: payload?.new?.created_at
          };
          callback?.(notification);
        }
      )
      ?.subscribe();

    return () => {
      subscription?.unsubscribe();
    };
  }
};

export default notificationService;